module Client.Admin.ManageNews.Grid

open Shared
open Client
open System
open Illy.SafeStack.Client
open Illy.SafeStack.Client.jQueryUI
open Fable.React
open Fable.React.Props
open Elmish
open Shared.Admin.ManageNews
open Shared.Admin.ManageNews.Grid
open SafeStack.Client

type Model = 
    {
        NewsArticles: NewsArticleDTO list
        Edit: Edit.Model option
        Delete: Delete.Model option
        TogglePublishing: NewsArticleId option
        Loading: bool
        Sorting: SortInfo
    }

type Event = 
    | CloseOverlay
    | RefreshPublishedNews
    | AppError of AppError

type Msg = 
    | ArticlesDataLoaded of NewsArticleDTO list
    | PublishingToggled
    | ApiError of ApiError
    | PublishArticle of NewsArticleId
    | UnpublishArticle of NewsArticleId
    | DeleteArticle of NewsArticleId
    | EditArticle of NewsArticleId
    | EditMsg of Edit.Msg
    | DeleteMsg of Delete.Msg
    | CloseOverlay
    | EndNotification
    | AddArticle
    | ReloadGrid
    | RaiseEvent of Event
    | ChangeSortInfo of SortInfo

module Calls =
    let loadGrid sortInfo =
        Api.secureCall<IManageNewsApi,_,_,_> SecuredRoute.Admin (fun api -> api.getGridData) sortInfo ArticlesDataLoaded Msg.ApiError

let init () =
    let sort =
        {
            Field = StartShowing
            Direction = Desc
        }
    {
        NewsArticles = []
        Loading = true
        Edit = None
        Delete = None
        TogglePublishing = None
        Sorting = sort
    }, Calls.loadGrid sort

let dispatchEditMsg model msg =
    match model.Edit with 
    | None -> model, SideEffect.none
    | Some ma -> 
        let m, eff = Edit.update ma msg 
        match eff with 
        | SideEffect.Cmd c -> {model with Edit = Some m}, Cmd.map EditMsg c |> SideEffect.Cmd 
        | SideEffect.Event ev -> 
            match ev with 
            | Edit.Event.AppError err -> {model with Edit = Some m}, err |> AppError |> SideEffect.Event
            | Edit.Event.CloseOverlay -> {model with Edit = None}, SideEffect.none
            | Edit.Event.SaveSuccessful notification -> 
                {model with 
                    Edit = None
                }, Cmd.batch [
                    Cmd.ofMsg ReloadGrid
                    Notification.success notification EndNotification
                ] |> SideEffect.Cmd

let dispatchDeleteMsg model msg =
    match model.Delete with 
    | None -> model, SideEffect.none
    | Some ma -> 
        let m, eff = Delete.update ma msg 
        match eff with 
        | SideEffect.Cmd c -> {model with Delete = Some m}, Cmd.map DeleteMsg c |> SideEffect.Cmd 
        | SideEffect.Event ev -> 
            match ev with 
            | Delete.Event.ApiError err -> {model with Delete = Some m}, err |> AppError.ApiError |> AppError |> SideEffect.Event
            | Delete.Event.CloseOverlay -> {model with Delete = None}, SideEffect.none
            | Delete.Event.DeleteSuccessful notification -> 
                {model with 
                    Delete = None
                }, Cmd.batch [
                    Cmd.ofMsg ReloadGrid
                    Notification.success notification EndNotification
                ] |> SideEffect.Cmd

let update msg model = 
    match msg with
    | RaiseEvent ev -> model, SideEffect.Event ev
    | ReloadGrid ->
        {model with Loading = true},
        Cmd.batch [
            Api.secureCall<IManageNewsApi,_,_,_> SecuredRoute.Admin (fun api -> api.getGridData) model.Sorting ArticlesDataLoaded Msg.ApiError
            Cmd.ofMsg (RaiseEvent RefreshPublishedNews)
        ] |> SideEffect.Cmd
    | ArticlesDataLoaded d -> 
        {model with 
            NewsArticles = d
            Loading = false
        }, SideEffect.none
    | Msg.ApiError err -> model, err |> AppError.ApiError |> AppError |> SideEffect.Event 
    | Msg.CloseOverlay -> model, Event.CloseOverlay |> SideEffect.Event
    | PublishArticle aId -> 
        {model with TogglePublishing = Some aId},
        Api.secureCall<IManageNewsApi,_,_,_> SecuredRoute.Admin (fun api -> api.publishArticle) aId (fun () -> PublishingToggled) Msg.ApiError
        |> SideEffect.Cmd
    | UnpublishArticle aId -> 
        {model with TogglePublishing = Some aId},
        Api.secureCall<IManageNewsApi,_,_,_> SecuredRoute.Admin (fun api -> api.unpublishArticle) aId (fun () -> PublishingToggled) Msg.ApiError
        |> SideEffect.Cmd
    | PublishingToggled ->
        {model with 
            TogglePublishing = None
        }, Cmd.ofMsg ReloadGrid |> SideEffect.Cmd
    | DeleteArticle aId -> 
        let m, c = Delete.init aId
        {model with Delete = Some m}, Cmd.map DeleteMsg c |> SideEffect.Cmd
    | EditArticle aId -> 
        let m, c = Edit.init (Some aId)
        {model with Edit = Some m}, Cmd.map EditMsg c |> SideEffect.Cmd
    | AddArticle -> 
        let m, c = Edit.init None
        {model with Edit = Some m}, Cmd.map EditMsg c |> SideEffect.Cmd
    | EditMsg eMsg -> dispatchEditMsg model eMsg
    | DeleteMsg eMsg -> dispatchDeleteMsg model eMsg
    | EndNotification -> model, SideEffect.none
    | ChangeSortInfo info ->
        {
            model with
                Sorting = info
                Loading = true
        }, Calls.loadGrid info |> SideEffect.Cmd

let sortHeader loading (currentSort: SortInfo) dispatch displayName fieldToSelect = 
    span [ 
        yield Style [
            Cursor "pointer"
        ]
        if not loading then 
            yield OnClick 
                (fun _ -> 
                    if currentSort.Field = fieldToSelect then 
                        dispatch (ChangeSortInfo { Field = fieldToSelect; Direction = SortDirection.toggle currentSort.Direction})
                    else 
                        dispatch (ChangeSortInfo { Field = fieldToSelect; Direction = SortDirection.Asc})
                )
    ] [
        yield str displayName
        if currentSort.Field = fieldToSelect then 
            match currentSort.Direction with 
            | SortDirection.Asc -> 
                yield FontAwesome.solid "chevron-up" [ FontAwesome.Fixed ] []
            | SortDirection.Desc -> 
                yield FontAwesome.solid "chevron-down" [ FontAwesome.Fixed ] []
    ]
    |> Tooltip.create [
        str "Click to sort by "
        str displayName
    ]

let private renderGridOverlay model dispatch = 
    jQueryUI.modal "Manage news articles" 50 Msg.CloseOverlay dispatch (Some 1000) [
        div [
            Id "AdminNewsArticlesGrid"
        ] [
            div [
                ClassName "gridControls"
            ] [
                span [] []
                UI.button "Add Article" AddArticle dispatch
            ]
            model.NewsArticles
            |> jQueryUI.simpleTable "No News Articles were found."
                [
                    None ===> fun x -> [ 
                        a [
                            OnClick (fun _ -> EditArticle (NewsArticleId x.Id) |> dispatch)
                        ] [
                            str "Edit"
                        ]
                    ]
                    Some [sortHeader model.Loading model.Sorting dispatch "Headline" Headline] ===> fun x -> [ str x.Headline ]
                    Some [sortHeader model.Loading model.Sorting dispatch "Start Showing" StartShowing] ===> fun x -> [ str (illyDate x.StartShowing) ]
                    Some [sortHeader model.Loading model.Sorting dispatch "Stop Showing" StopShowing] ===> fun x -> [ x.StopShowing |> Option.map illyDate  |> Option.defaultValue "Not Set" |> str ]
                    Some [sortHeader model.Loading model.Sorting dispatch "Published" Published] ===> fun x -> [ 
                        FontAwesome.solid "circle" [] [
                            Style [
                                if x.Published then yield Color "hsl(120deg 100% 33%)"
                                else yield Color "hsl(0deg 100% 47%)"
                            ]
                        ]
                    ]
                    Some [ str "Actions" ] ===> fun x -> [ 
                        if Some (NewsArticleId x.Id) = model.TogglePublishing then jQueryUI.saveSpinner
                        else 
                            if x.Published then UI.buttonWithDisabledAndClasses model.TogglePublishing.IsSome "fixedWidth" "Unpublish" (UnpublishArticle (NewsArticleId x.Id)) dispatch
                            else UI.buttonWithDisabledAndClasses model.TogglePublishing.IsSome "fixedWidth" "Publish" (PublishArticle (NewsArticleId x.Id)) dispatch
                        str " or "
                        a [
                            OnClick (fun _ -> DeleteArticle (NewsArticleId x.Id) |> dispatch)
                        ] [
                            str "Delete"
                        ]
                    ]
                ]
                model.Loading
        ]
    ]

let view model dispatch = 
    [
        if model.Edit.IsNone && model.Delete.IsNone then 
            yield! renderGridOverlay model dispatch
         
        yield! jQueryUI.conditionallyRenderView model.Edit Edit.view EditMsg dispatch
        yield! jQueryUI.conditionallyRenderView model.Delete Delete.view DeleteMsg dispatch
    ]